import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useToast } from '../contexts/ToastContext';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { track } from '../utils/analytics';
import {
    BACKUP_TOOLS, backupToolLabel, formatBytes, formatTimeAgo,
    restoreFilesWithPrompt, completionPct, restoreCoverageGap, restoreCoverageNote, mergeRestoreNote, availabilityNote,
    mergeUpToDate, mergeRecordEntry, mergeDeltaLine,
} from '../utils/cloudBackup';
import { setRestoreWizardOpen } from '../utils/backupToastVisibility';
import './BackupRestoreModal.scss';

// This machine's merge record for a DJ app. The preview's copy wins: the
// engine fills in the per-app markers for records written before they
// existed (from the snapshot list), so a merge made last week still lets
// the wizard say "nothing new for this app" today.
const mergeEntryFor = (ref, preview, tool) => preview?.mergeHistory?.[tool] || ref.current?.[tool];

/**
 * Cloud Backup restore wizard.
 *
 * Opens straight on the guided "Bring back what's missing" scope screen —
 * there is no mode chooser. ADD-ONLY end to end: music files land only where
 * they're absent (verified by hash), playlists gain their missing tracks
 * without losing any, and cues are added only to tracks that have none.
 * Cross-platform — a backup made on Windows restores on a Mac and vice versa
 * (files are re-rooted, DJ data follows them).
 *
 * The one other path (kind 'dj_data') is reached only via a per-app
 * "Restore" link in Settings (`initialTool`): a byte-exact copy of that DJ
 * app's library, downloaded as files or applied in place. Applying is the
 * destructive path, so it demands the tool's name typed out; every existing
 * file is renamed to a timestamped safety copy first (never deleted) by the
 * backend.
 *
 * Desktop only — mounted behind a `window.electron?.backup` guard.
 */
const backupBridge = () => window.electron?.backup;


// `restoreRunningElsewhere`: a restore is active in the main process that THIS
// modal instance didn't start (Settings and the Backup page each mount their
// own copy of this wizard). Starting a second one would race the first over
// the same files, so the scope screen holds until it finishes.
function BackupRestoreModal({ show, onHide, initialTool = null, restoreRunningElsewhere = false }) {
    // Steps: 2 = scope (missing) / snapshot picker (dj_data),
    // 3 = run + result. (Step 1 was the retired mode chooser; the numbering
    // stays so the two flows share the run/result step.)
    const [step, setStep] = useState(2);
    const [kind, setKind] = useState('missing');
    const [snapshots, setSnapshots] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [snapshotId, setSnapshotId] = useState(null);
    const [destMode, setDestMode] = useState('files'); // dj_data: 'files' | 'apply'
    const [destDir, setDestDir] = useState(null);
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    // Stop was clicked and the engine is winding down (the file in flight
    // finishes so nothing is left truncated). Cleared when the run settles.
    const [stopping, setStopping] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    // 'missing' flow: preview facts + the user's scope choices.
    // undefined = still loading (the scope screen shows skeleton cards so the
    // window opens at its final size instead of growing when the preview
    // arrives); null = the preview call itself failed.
    const [preview, setPreview] = useState(undefined);
    const [scopeDj, setScopeDj] = useState({}); // tool -> bool
    const [scopeFiles, setScopeFiles] = useState(false);
    // The MixGoat library itself (enriched track rows + playlists). Default:
    // ticked when the backup's library holds more tracks than this machine's —
    // the fresh-install case the row exists for.
    const [scopeLibrary, setScopeLibrary] = useState(false);
    const [missingPhase, setMissingPhase] = useState(null); // 'files' | 'library' | 'dj' while running
    const dispatch = useDispatch();
    // Source machine for the guided restore. null = newest snapshot per tool
    // regardless of origin — which is also the trap this picker exists for: a
    // machine that backed itself up an hour ago shadows every other machine's
    // snapshots. `machines` lives outside `preview` so the picker doesn't
    // unmount while a re-scoped preview loads.
    const [sourceHost, setSourceHost] = useState(null);
    const [machines, setMachines] = useState([]);

    // A restore runs in the MAIN process — closing this window doesn't stop
    // it, so the window is closable mid-run. These refs let the still-running
    // promise know whether the user is looking (finish quietly in the modal)
    // or has moved on (announce completion app-wide), and let a reopen show
    // the live run instead of resetting it.
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const showRef = useRef(show);
    showRef.current = show;
    const busyRef = useRef(false);
    useEffect(() => { busyRef.current = busy; }, [busy]);
    // The app-wide progress toast carries restore progress too, but stays out
    // of the way while THIS window (the primary progress view) is on screen.
    // Closing mid-run flips the flag and the toast appears — live proof the
    // restore continues, in place of any parting message.
    useEffect(() => {
        setRestoreWizardOpen(!!show);
        return () => setRestoreWizardOpen(false);
    }, [show]);
    const notifyDone = (title, message) => {
        if (showRef.current) return; // the user is watching this window — it reports
        toastRef.current?.card?.({ title, message, duration: 7000 });
    };

    // Guards: gap analytics fires once per wizard open; a stale re-scoped
    // preview (user switched machines twice, slow first response) must not
    // clobber the newer one.
    const gapTrackedRef = useRef(false);
    const availTrackedRef = useRef(false);
    const previewReqRef = useRef(0);
    // Last successful DJ-data merge per tool (settings.lastMergeDj) — read
    // once per open, BEFORE the (slow, network) preview resolves, so
    // applyPreview can default already-merged tools unchecked. A ref, not
    // state: applyPreview is a stable callback.
    const lastMergeDjRef = useRef({});

    const applyPreview = useCallback((p) => {
        if (!p || p.error) { setPreview(null); return; }
        setPreview(p);
        if (Array.isArray(p.machines)) setMachines(p.machines);
        const gap = restoreCoverageGap(p);
        if (gap && !gapTrackedRef.current) {
            gapTrackedRef.current = true;
            track('backup_restore_gap_notice_shown', {
                files_total: gap.filesTotal, model_tracks: gap.modelTracks,
                library_tracks: gap.libraryTracks, reason: gap.reason,
            });
        }
        const detected = p.detected || {};
        const dj = {};
        for (const { tool } of BACKUP_TOOLS) {
            // A target is pre-ticked when it's installed here, the backup
            // holds data for it, AND the merge would bring something new —
            // a tool that already merged this exact snapshot defaults off
            // (its row says so; it can still be re-checked). Same for a tool
            // NONE of whose music is in the backup: its merge is a
            // guaranteed no-op, and its row says why.
            const avail = p.model?.availability?.[tool];
            const fullyUnavailable = !!avail && avail.tracks > 0 && avail.unavailable === avail.tracks;
            dj[tool] = !!detected[tool] && toolHasBackup(p, tool)
                && !mergeUpToDate(mergeEntryFor(lastMergeDjRef, p, tool), p, tool)
                && !fullyUnavailable;
        }
        setScopeDj(dj);
        setScopeFiles((p.files?.missing || 0) > 0);
        const lib = p.model?.counts?.library;
        setScopeLibrary(!!p.model?.hasLibrary && !!lib
            && (lib.tracks || 0) > (p.model?.libraryLocalTracks ?? 0));
        const unavailable = Object.entries(p.model?.availability || {})
            .filter(([tool, a]) => detected[tool] && a.unavailable > 0);
        if (unavailable.length && !availTrackedRef.current) {
            availTrackedRef.current = true;
            track('backup_restore_unavailable_notice_shown', {
                tool_count: unavailable.length,
                tracks_unavailable: unavailable.reduce((n, [, a]) => n + a.unavailable, 0),
                full_tools: unavailable.filter(([, a]) => a.tracks > 0 && a.unavailable === a.tracks).length,
            });
        }
    }, []);

    const reset = useCallback(() => {
        setStep(2); setKind('missing'); setSnapshotId(null); setDestMode('files');
        setDestDir(null); setConfirmText('');
        setBusy(false); setStopping(false); setProgress(null); setResult(null); setLoadError(null);
        setPreview(undefined); setScopeDj({}); setScopeFiles(false); setScopeLibrary(false);
        setMissingPhase(null);
        setSourceHost(null); setMachines([]);
        gapTrackedRef.current = false;
        availTrackedRef.current = false;
    }, []);

    useEffect(() => {
        if (!show) return undefined;
        // Reopened while a restore is still running: keep the live run's
        // state (step, progress, scope) — only the progress subscription
        // below needs re-attaching. A reset here would blank a run that is
        // still happening in the main process.
        if (busyRef.current) {
            const off = backupBridge()?.onProgress?.((p) => setProgress(p));
            return () => { if (off) off(); };
        }
        reset();
        // Per-app "Restore" entry point (BackupSection app cards): the
        // byte-exact flow for that one tool, set before any await so the
        // guided scope screen never flashes first.
        if (initialTool) setKind('dj_data');
        let cancelled = false;
        backupBridge()?.listSnapshots().then((res) => {
            if (cancelled) return;
            if (res?.error) { setLoadError(res.error); return; }
            const snaps = res?.snapshots || [];
            setSnapshots(snaps);
            // Preselect that tool's newest DJ-data snapshot and land on the
            // destination step; with none stored, fall back to the picker.
            if (initialTool) {
                const newest = snaps
                    .filter((s) => s.tool === initialTool && s.storedAt)
                    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0];
                if (newest) { setSnapshotId(newest.id); setStep(3); }
            }
        });
        // The preview powers the scope screen — it loads on open. Every exit
        // MUST settle preview out of `undefined` — that's the skeleton state,
        // and an unsettled promise (or an old preload without restorePreview)
        // would leave the ghost cards pulsing forever.
        // Settings are a fast local IPC; the preview is a network call —
        // this lands first, so applyPreview sees the merge history.
        backupBridge()?.getStatus?.()?.then?.((st) => {
            if (!cancelled) lastMergeDjRef.current = st?.settings?.lastMergeDj || {};
        })?.catch?.(() => {});
        const reqId = ++previewReqRef.current;
        const previewCall = backupBridge()?.restorePreview?.();
        if (previewCall?.then) {
            previewCall.then((p) => {
                if (cancelled || previewReqRef.current !== reqId) return;
                applyPreview(p);
            }).catch(() => { if (!cancelled && previewReqRef.current === reqId) setPreview(null); });
        } else {
            setPreview(null);
        }
        const off = backupBridge()?.onProgress?.((p) => setProgress(p));
        return () => { cancelled = true; if (off) off(); };
    }, [show, reset, initialTool, applyPreview]);

    // Re-scope the preview to one machine's snapshots (or back to
    // latest-of-each). Scope choices reset with it — they describe the
    // previous machine's backup.
    const pickSource = (host) => {
        const value = host || null;
        setSourceHost(value);
        setPreview(undefined); // skeletons while the re-scoped preview loads
        track('backup_restore_source_picked', {
            machines: machines.length,
            latest: !value,
        });
        const reqId = ++previewReqRef.current;
        const call = backupBridge()?.restorePreview?.(value ? { hostname: value } : undefined);
        if (call?.then) {
            call.then((p) => { if (previewReqRef.current === reqId) applyPreview(p); })
                .catch(() => { if (previewReqRef.current === reqId) setPreview(null); });
        } else {
            setPreview(null);
        }
    };

    const visibleSnapshots = useMemo(() => {
        if (!snapshots) return null;
        return snapshots
            .filter((s) => s.tool !== 'model' && s.storedAt)
            .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }, [snapshots]);

    const selected = useMemo(
        () => (snapshots || []).find((s) => s.id === snapshotId) || null,
        [snapshots, snapshotId]
    );

    const pickFolder = async () => {
        const res = await window.electron.dialog.showOpenDialog({
            properties: ['openDirectory', 'createDirectory'],
            title: 'Choose a folder for the restored files',
        });
        const dir = res?.filePaths?.[0];
        if (dir) setDestDir(dir);
    };

    const applyToolLabel = selected ? backupToolLabel(selected.tool) : null;
    const needsTypedConfirm = destMode === 'apply';
    const confirmOk = !needsTypedConfirm
        ? !!destDir
        : !!applyToolLabel && confirmText.trim().toLowerCase() === applyToolLabel.toLowerCase();

    const runRestore = async () => {
        if (busy || !selected) return;
        setBusy(true);
        setResult(null);
        track('backup_restore_started', { kind: 'dj_data', tool: selected.tool });
        try {
            const res = await backupBridge().restoreSnapshot({
                snapshotId: selected.id,
                mode: destMode,
                destDir: destMode === 'files' ? destDir : undefined,
            });
            if (res?.error) {
                setResult({ ok: false, error: res.error });
                notifyDone('Restore failed', res.error);
            } else {
                setResult({ ok: true, ...res });
                track('backup_restore_completed', {
                    kind: 'dj_data',
                    tool: selected.tool,
                    // Apply mode: how many library entries were repointed to
                    // this machine's file locations after the DB landed.
                    relinked: res.relink?.results?.[0]?.updated ?? 0,
                });
                notifyDone('Restore complete', `${backupToolLabel(selected.tool)} is ready`);
            }
        } catch (err) {
            setResult({ ok: false, error: err.message });
            notifyDone('Restore failed', err.message);
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    // ── The guided ADD-ONLY restore: files first (recording where each one
    // landed), then the DJ-data merge rides those moves so the backup's paths
    // — possibly from another OS — resolve to real local files. ──
    const missingTargets = BACKUP_TOOLS.filter(({ tool }) => scopeDj[tool]).map(({ tool }) => tool);
    const runMissingRestore = async () => {
        if (busy || (!scopeFiles && !missingTargets.length && !scopeLibrary)) return;
        setBusy(true);
        setResult(null);
        setStep(3);
        track('backup_restore_missing_started', {
            files: scopeFiles,
            library: scopeLibrary,
            dj_targets: missingTargets.length,
            // Detected apps the wizard judged current (nothing new for them
            // since this machine last merged) and so left unticked.
            dj_up_to_date: BACKUP_TOOLS.filter(({ tool }) => preview?.detected?.[tool]
                && mergeUpToDate(mergeEntryFor(lastMergeDjRef, preview, tool), preview, tool)).length,
        });
        // Durable "a restore is underway" marker + its SCOPE: if the app dies
        // mid-run, the next launch silently RESUMES it (autoResumeRestore) —
        // the prompt is only the fallback. Cleared on success below. destDir
        // is filled in once the files phase learns it.
        backupBridge().setEnabled({
            restorePending: true,
            restorePendingScope: { files: scopeFiles, library: scopeLibrary, targets: missingTargets, destDir: null, destDirMode: null },
            restoreAutoAttempts: 0,
        }).catch(() => {});
        const summary = { files: null, library: null, dj: null };
        // The catch below cannot read `missingPhase` — that closure holds the
        // value from before this run started. Mirror it in a local.
        let phase = 'setup';
        try {
            let moves = [];
            if (scopeFiles) {
                setMissingPhase('files'); phase = 'files';
                const filesRes = await backupBridge().listFiles();
                if (filesRes?.error) throw new Error(filesRes.error);
                const items = (filesRes?.files || []).map((f) => ({
                    sha256: f.sha256, filePath: f.filePath, fileName: f.fileName, sizeBytes: f.sizeBytes,
                }));
                if (items.length) {
                    const res = await restoreFilesWithPrompt(items);
                    if (res?.cancelled) { setBusy(false); setMissingPhase(null); setStep(2); return; }
                    if (res?.error) throw new Error(res.error);
                    summary.files = res;
                    moves = res?.moves || [];
                    if (res?.destDir) {
                        // Remember the folder for a silent resume — orphans
                        // can't go home, and startup must not open a picker.
                        backupBridge().setEnabled({
                            restorePendingScope: { files: scopeFiles, library: scopeLibrary, targets: missingTargets, destDir: res.destDir, destDirMode: 'orphans' },
                        }).catch(() => {});
                    }
                    if (res?.restored?.length) track('backup_file_restored', { count: res.restored.length, from: 'missing_flow' });
                    if (res?.stopped) {
                        // The user stopped it — a decision, not a failure. The
                        // DJ-data merge is skipped (it would only see part of
                        // the files), and the next launch doesn't nag about an
                        // unfinished restore: Restore here resumes it anyway.
                        setResult({ ok: true, stoppedEarly: true, missing: summary });
                        backupBridge().setEnabled({ restorePending: false, restorePendingScope: null, restoreAutoAttempts: 0, restorePromptDismissed: true }).catch(() => {});
                        const n = (res.restored || []).length;
                        notifyDone('Restore stopped', `${n.toLocaleString()} ${n === 1 ? 'file' : 'files'} restored before stopping — run Restore again to finish.`);
                        return;
                    }
                }
            }
            // MixGoat's own library rides along automatically whenever there's
            // a portable backup: enriched track rows and playlists merge back
            // ADD-ONLY, so restored music shows up in the Library itself — not
            // just on disk and in the DJ apps. A failure here must not sink
            // the restore that already landed; the result screen reports it.
            if (scopeLibrary && preview?.model && backupBridge().restoreMergeLibrary) {
                setMissingPhase('library'); phase = 'library';
                try {
                    const libRes = await backupBridge().restoreMergeLibrary({
                        snapshotId: preview.model.snapshotId,
                        pathMoves: moves,
                    });
                    if (libRes?.error) throw new Error(libRes.error);
                    summary.library = libRes || null;
                    if (libRes && !libRes.unsupported) {
                        track('backup_library_restored', {
                            tracks_added: libRes.tracksAdded || 0,
                            tracks_enriched: libRes.tracksEnriched || 0,
                            playlists_created: libRes.playlistsCreated || 0,
                            playlist_tracks_added: libRes.playlistTracksAdded || 0,
                            from: 'missing_flow',
                        });
                        if ((libRes.tracksAdded || 0) + (libRes.playlistsCreated || 0) + (libRes.playlistsUpdated || 0) > 0) {
                            dispatch(triggerPlaylistRefresh());
                        }
                    }
                } catch (err) {
                    console.error('[backup] MixGoat library merge failed:', err.message);
                    track('backup_restore_missing_failed', {
                        phase: 'library',
                        error: String(err.message || err).slice(0, 200),
                    });
                    summary.library = { error: err.message };
                }
            }
            if (missingTargets.length) {
                setMissingPhase('dj'); phase = 'dj';
                const res = await backupBridge().restoreMergeDj({
                    snapshotId: preview?.model?.snapshotId,
                    targets: missingTargets,
                    pathMoves: moves,
                });
                if (res?.error) throw new Error(res.error);
                summary.dj = res;
                // Remember which snapshot each target just merged (and how
                // much music was still missing), so the next wizard open says
                // "nothing new since" instead of re-offering the same merge —
                // per APP, via the markers mergeRecordEntry pulls from the
                // preview, so one app's change doesn't re-arm the other three.
                try {
                    const missingAfter = summary.files
                        ? (summary.files.failed || []).length
                        : (preview?.files?.missing ?? 0);
                    const merged = { ...lastMergeDjRef.current };
                    const mergedSnapshotId = res.snapshotId || preview?.model?.snapshotId || null;
                    const at = Date.now();
                    for (const t of missingTargets) {
                        merged[t] = mergeRecordEntry(preview, t, { snapshotId: mergedSnapshotId, filesMissing: missingAfter, at });
                    }
                    lastMergeDjRef.current = merged;
                    backupBridge().setEnabled({ lastMergeDj: merged }).catch(() => {});
                } catch { /* bookkeeping only */ }
            }
            setResult({ ok: true, missing: summary });
            backupBridge().setEnabled({ restorePending: false, restorePendingScope: null, restoreAutoAttempts: 0, restorePromptDismissed: true }).catch(() => {});
            track('backup_restore_missing_completed', {
                files_restored: summary.files?.restored?.length || 0,
                files_failed: summary.files?.failed?.length || 0,
                dj_targets: missingTargets.length,
                // Targets whose own content is entirely absent from this
                // machine — they merged nothing (no cross-tool fallback).
                empty_targets: (summary.dj?.targets || []).filter((t) => t.nothingToMerge).length,
            });
            const bits = [];
            if (summary.files) {
                bits.push(`${(summary.files.restored || []).length.toLocaleString()} files restored`);
                if ((summary.files.failed || []).length) bits.push(`${summary.files.failed.length.toLocaleString()} failed`);
            }
            if (summary.library && !summary.library.error && !summary.library.unsupported
                && (summary.library.tracksAdded || summary.library.tracksEnriched || summary.library.playlistsCreated || summary.library.playlistsUpdated)) {
                bits.push('MixGoat library updated');
            }
            if (summary.dj?.targets?.length) {
                bits.push(`${summary.dj.targets.map((t) => backupToolLabel(t.target)).join(', ')} updated`);
            }
            notifyDone('Restore complete', bits.join(' · ') || 'Everything was already on this computer.');
        } catch (err) {
            // MG-1015: the failure used to exist ONLY in this toast — nothing
            // in the logs or analytics said what went wrong, or even that it
            // went wrong. Log + track so the next report carries the cause.
            console.error(`[backup] guided restore failed in the ${phase} phase:`, err.message);
            track('backup_restore_missing_failed', {
                phase,
                error: String(err.message || err).slice(0, 200),
            });
            setResult({ ok: false, error: err.message });
            notifyDone('Restore failed', err.message);
        } finally {
            setBusy(false);
            setStopping(false);
            setProgress(null);
            setMissingPhase(null);
        }
    };

    // Fire-and-forget: the engine stops between files, and the running
    // restoreFiles call above returns with `stopped: true` when it does.
    const stopRestore = () => {
        if (stopping) return;
        setStopping(true);
        track('backup_restore_stopped', { from: 'wizard' });
        backupBridge()?.restoreStop?.()?.catch?.(() => {});
    };

    if (!show) return null;

    const previewHasContent = preview && !preview.error
        && (!!preview.model || (preview.tools || []).length > 0 || (preview.files?.total || 0) > 0);
    // Only DJ apps that are installed here AND present in the backup get a
    // row — an installed app the backup holds nothing for has nothing to
    // bring back, so listing it would only invite a no-op tick.
    const detectedTargets = BACKUP_TOOLS.filter(
        ({ tool }) => preview?.detected?.[tool] && toolHasBackup(preview, tool)
    );

    const progressTotal = progress?.totalBytes || progress?.bytesTotal || 0;
    const progressPercent = progress && progressTotal
        ? Math.min(100, Math.round(((progress.bytesReceived || 0) / progressTotal) * 100))
        : null;
    const progressLabel = !progress ? null
        : progress.phase === 'downloading' ? 'Downloading and verifying'
        : progress.phase === 'files-restore' ? (progress.total
            ? `Downloading and verifying (${Math.min((progress.done || 0) + 1, progress.total)} of ${progress.total})`
            : 'Downloading and verifying')
        : progress.phase === 'decompressing' ? 'Unpacking'
        : progress.phase === 'applying' ? 'Applying'
        : progress.phase === 'writing' ? 'Writing library'
        : null;

    // Which machine's backup to restore from — shown only when more than one
    // machine has snapshots, because with one there is nothing to choose and
    // with two the newest-wins default silently shadows the other (a machine
    // that just backed itself up out-offers the machine the user actually
    // wants to restore).
    const sourcePicker = machines.length > 1 ? (
        <label className="brm-source-row">
            <span className="brm-muted">Restore from</span>
            {/* Native <select> by choice (Erez, 2026-09-01): CustomSelect's
                open list reads as clutter here — the quiet native control
                fits the wizard better. */}
            <select
                className="brm-source-select"
                value={sourceHost || ''}
                onChange={(e) => pickSource(e.target.value || null)}
                disabled={busy}
            >
                <option value="">Latest from each computer</option>
                {machines.map((m) => (
                    <option key={m.hostname} value={m.hostname}>
                        {m.hostname} — backed up {formatTimeAgo(new Date(m.at).getTime()) || 'recently'}
                    </option>
                ))}
            </select>
        </label>
    ) : null;

    return (
        <div className="backup-restore-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onHide(); }}>
            <div className="backup-restore-modal" role="dialog" aria-label="Restore from Cloud Backup">
                <div className="brm-header">
                    <span className="brm-title">Restore from Cloud Backup</span>
                    {/* Closable mid-run on purpose: the restore runs in the
                        main process and keeps going — completion is announced
                        app-wide when the window is closed. */}
                    <button type="button" className="brm-close" onClick={onHide} aria-label="Close">×</button>
                </div>

                <div className="brm-body">
                    {step === 2 && kind === 'missing' && preview === undefined && (
                        <>
                            <p className="brm-lead">What should come back?</p>
                            {sourcePicker}
                            {/* Quiet skeleton (FeedTab recipe) sized like the
                                loaded list, so the window opens at its final
                                size instead of growing when the preview
                                arrives. */}
                            <div className="brm-skeletons" aria-hidden="true">
                                <div className="brm-skel-card" />
                                <div className="brm-skel-card" />
                                <div className="brm-skel-card" />
                            </div>
                        </>
                    )}

                    {step === 2 && kind === 'missing' && preview === null && (
                        <>
                            <p className="brm-lead">What should come back?</p>
                            <p className="brm-error">Couldn&apos;t check your backup. Close this window and try again.</p>
                        </>
                    )}

                    {step === 2 && kind === 'missing' && preview && !previewHasContent && (
                        <>
                            <p className="brm-lead">What should come back?</p>
                            {sourcePicker}
                            <p className="brm-muted">
                                Nothing to restore yet — turn on Cloud Backup and run &ldquo;Back up now&rdquo; first.
                            </p>
                        </>
                    )}

                    {step === 2 && kind === 'missing' && previewHasContent && (
                        <>
                            <p className="brm-lead">What should come back?</p>
                            {sourcePicker}
                            <div className="brm-scope-group">
                                <p className="brm-muted brm-group-label">Music files</p>
                                {preview?.files?.total > 0 && preview.files.missing === 0 ? (
                                    // Everything's here → a verified STATUS, not an
                                    // offer. A checkbox whose restore would download
                                    // nothing is an actionable-looking no-op; a green
                                    // check says "checked, and you're covered".
                                    <div className="brm-files-ok">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                                            <path d="M5 8.2l2 2 4-4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span>
                                            <span className="brm-option-title">All {preview.files.total.toLocaleString()} backed-up files are on this computer</span>
                                            <span className="brm-option-detail">Nothing to download — your music is here and in the cloud.</span>
                                        </span>
                                    </div>
                                ) : (
                                    <label className={`brm-radio ${!(preview?.files?.total > 0) ? 'brm-disabled' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={scopeFiles}
                                            disabled={!(preview?.files?.total > 0)}
                                            onChange={() => setScopeFiles((v) => !v)}
                                        />
                                        <span>
                                            <span className="brm-option-title">My backed-up music</span>
                                            <span className="brm-option-detail">
                                                {!(preview?.files?.total > 0)
                                                    ? 'No music files in your backup yet'
                                                    : `${preview.files.missing.toLocaleString()} of ${preview.files.total.toLocaleString()} backed-up files aren’t on this computer · ${formatBytes(preview.files.totalBytes)} total`}
                                            </span>
                                        </span>
                                    </label>
                                )}
                                {(() => {
                                    // The lopsided-backup note: the snapshots
                                    // reference thousands of tracks the cloud
                                    // holds no files for. Without it this
                                    // screen shows "180 files" over "4,609
                                    // tracks" and lets the reader conclude
                                    // either number is a bug. The wording
                                    // names the real reason — usually "never
                                    // imported into MixGoat", which is by
                                    // design, not a failed backup. Warning
                                    // tone ONLY when the backup is KNOWN to
                                    // be behind; a by-design or ambiguous gap
                                    // is explanation, not alarm.
                                    const gap = restoreCoverageGap(preview);
                                    const note = restoreCoverageNote(gap);
                                    if (!note) return null;
                                    const alarming = gap.reason === 'behind' || gap.reason === 'both';
                                    return <p className={`brm-note-gap${alarming ? '' : ' brm-note-gap--info'}`}>{note}</p>;
                                })()}
                            </div>
                            {preview?.model && detectedTargets.length > 0 && (
                                <div className="brm-scope-group">
                                    <p className="brm-muted brm-group-label">DJ app data — missing playlists, tracks and cues</p>
                                    {detectedTargets.map(({ tool, label }) => {
                                        const entry = mergeEntryFor(lastMergeDjRef, preview, tool);
                                        const upToDate = mergeUpToDate(entry, preview, tool);
                                        const availNote = availabilityNote(preview.model?.availability?.[tool]);
                                        return (
                                            <label key={tool} className="brm-radio">
                                                <input
                                                    type="checkbox"
                                                    checked={!!scopeDj[tool]}
                                                    onChange={() => setScopeDj((s) => ({ ...s, [tool]: !s[tool] }))}
                                                />
                                                <span>
                                                    <span className="brm-option-title">{label}</span>
                                                    <span className="brm-option-detail">
                                                        {upToDate
                                                            ? `Merged ${formatTimeAgo(entry.at)} — nothing new in the backup since`
                                                            : (() => {
                                                                const counts = preview.model?.counts?.perTool?.[tool];
                                                                const from = preview.model?.hostname;
                                                                const when = preview.model?.at ? formatTimeAgo(new Date(preview.model.at).getTime()) : null;
                                                                const totals = counts
                                                                    ? `${counts.playlists.toLocaleString()} ${counts.playlists === 1 ? 'playlist' : 'playlists'} · ${counts.tracks.toLocaleString()} tracks in the backup`
                                                                    : 'From your portable backup';
                                                                // Merged here before: lead with what is NEW
                                                                // since — a 7-track playlist reads as "+1
                                                                // playlist · +7 tracks", not as the whole
                                                                // library on offer again.
                                                                const delta = entry ? mergeDeltaLine(entry, counts, formatTimeAgo(entry.at)) : null;
                                                                const what = !delta ? totals : (delta.grown ? delta.text : `${delta.text} · ${totals}`);
                                                                const src = [from, when ? `backed up ${when}` : null].filter(Boolean).join(', ');
                                                                return src ? `${what} — ${src}` : what;
                                                            })()}
                                                    </span>
                                                    {availNote && (
                                                        <span className={`brm-option-warn${availNote.alarm ? ' brm-option-warn--alarm' : ''}`}>
                                                            {availNote.text}
                                                        </span>
                                                    )}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                            {preview?.model && detectedTargets.length === 0 && (
                                <p className="brm-muted">
                                    None of your backed-up DJ apps were found on this computer — your DJ data stays
                                    safe in the backup.
                                </p>
                            )}
                            {preview?.model?.hasLibrary && (
                                <div className="brm-scope-group">
                                    <p className="brm-muted brm-group-label">MixGoat library</p>
                                    <label className="brm-radio">
                                        <input
                                            type="checkbox"
                                            checked={scopeLibrary}
                                            onChange={() => setScopeLibrary((v) => !v)}
                                        />
                                        <span>
                                            <span className="brm-option-title">My MixGoat library</span>
                                            <span className="brm-option-detail">
                                                {(() => {
                                                    const lib = preview.model?.counts?.library;
                                                    const what = lib
                                                        ? `${lib.tracks.toLocaleString()} ${lib.tracks === 1 ? 'track' : 'tracks'} · ${lib.playlists.toLocaleString()} ${lib.playlists === 1 ? 'playlist' : 'playlists'}`
                                                        : 'Tracks and playlists';
                                                    return `${what}, with their enrichment — genres, keys, release dates and Mixpedia matches`;
                                                })()}
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            )}
                            <p className="brm-muted">
                                Add-only: files already here are verified and kept, your MixGoat library gets its
                                tracks, playlists and enrichment back, DJ playlists gain their missing tracks
                                without losing any, and cues land only on tracks that have none.
                            </p>
                        </>
                    )}

                    {step === 3 && kind === 'missing' && !result && (
                        <>
                            <p className="brm-lead">
                                {missingPhase === 'dj'
                                    ? 'Adding what’s missing to your DJ apps'
                                    : missingPhase === 'library'
                                        ? 'Updating your MixGoat library'
                                        : 'Bringing your music back'}
                            </p>
                            <div className="brm-progress">
                                <span className="brm-muted">
                                    {missingPhase === 'dj'
                                        ? (progress?.migration?.message
                                            || (progress?.phase === 'downloading' ? 'Downloading your backup' : 'Merging'))
                                        : missingPhase === 'library'
                                            ? (progress?.phase === 'downloading' ? 'Downloading your backup' : 'Adding your tracks, playlists and enrichment')
                                        : stopping ? 'Stopping — finishing the files in flight'
                                        : progress?.phase === 'files-restore' && progress?.stage === 'checking'
                                            ? `Checking what’s already here${progress.total ? ` (${(progress.done || 0).toLocaleString()} of ${progress.total.toLocaleString()})` : ''}`
                                        : (progress?.phase === 'files-restore' && progress?.total
                                            ? `Downloading and verifying (${Math.min((progress.done || 0) + 1, progress.total)} of ${progress.total})`
                                            : 'Checking what’s already here')}
                                </span>
                                <div className="brm-progress-track">
                                    <div
                                        className="brm-progress-fill"
                                        style={{
                                            // Files leg: bytes, fractional — a
                                            // per-file count only moved on each
                                            // completion, so the bar sat still
                                            // and then jumped. The checking pass
                                            // gets no fill: its counts are
                                            // verified files, and a bar that
                                            // resets when downloads begin reads
                                            // as losing progress.
                                            width: `${missingPhase === 'dj'
                                                ? (progress?.migration?.progress ?? 0)
                                                : progress?.stage === 'checking'
                                                    ? 0
                                                : progress?.bytesTotal
                                                    ? Math.min(100, ((progress.bytesReceived || 0) / progress.bytesTotal) * 100).toFixed(2)
                                                    : (completionPct(progress?.done, progress?.total) ?? 0)}%`,
                                        }}
                                    />
                                </div>
                            </div>
                            <p className="brm-muted">You can keep using MixGoat — closing this window won’t stop the restore, and you’ll be told when it’s done.</p>
                        </>
                    )}

                    {step === 2 && kind === 'dj_data' && (
                        <>
                            <p className="brm-lead">Pick a backup</p>
                            {loadError && <p className="brm-error">Couldn&apos;t load your backups: {loadError}</p>}
                            {!loadError && visibleSnapshots === null && <p className="brm-muted">Loading your backups…</p>}
                            {visibleSnapshots && visibleSnapshots.length === 0 && (
                                <p className="brm-muted">
                                    No DJ data backups yet. Turn on Cloud Backup and run &ldquo;Back up now&rdquo; first.
                                </p>
                            )}
                            {visibleSnapshots && visibleSnapshots.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={`brm-option ${snapshotId === s.id ? 'selected' : ''}`}
                                    onClick={() => { setSnapshotId(s.id); setConfirmText(''); setStep(3); }}
                                >
                                    <span className="brm-option-title">{backupToolLabel(s.tool)}</span>
                                    <span className="brm-option-detail">
                                        {new Date(s.createdAt).toLocaleString()} · {formatBytes(s.totalBytes)}
                                        {s.meta?.hostname ? ` · from ${s.meta.hostname}` : ''}
                                        {s.meta?.counts ? ` · ${s.meta.counts.playlists.toLocaleString()} ${s.meta.counts.playlists === 1 ? 'playlist' : 'playlists'}` : ''}
                                    </span>
                                </button>
                            ))}
                        </>
                    )}

                    {step === 3 && kind === 'dj_data' && selected && !result && (
                        <>
                            <p className="brm-lead">Where should the {backupToolLabel(selected.tool)} backup go?</p>
                            <label className="brm-radio">
                                <input type="radio" name="brm-dest" checked={destMode === 'files'} onChange={() => setDestMode('files')} />
                                <span>
                                    <span className="brm-option-title">Download as files</span>
                                    <span className="brm-option-detail">Save the library files to a folder you choose. Safe — nothing else is touched.</span>
                                </span>
                            </label>
                            {destMode === 'files' && (
                                <div className="brm-folder-row">
                                    <button type="button" className="brm-secondary-btn" onClick={pickFolder}>Choose folder…</button>
                                    <span className="brm-folder-path">{destDir || 'No folder selected'}</span>
                                </div>
                            )}
                            <label className="brm-radio">
                                <input type="radio" name="brm-dest" checked={destMode === 'apply'} onChange={() => setDestMode('apply')} />
                                <span>
                                    <span className="brm-option-title">Apply to {backupToolLabel(selected.tool)}</span>
                                    <span className="brm-option-detail">
                                        Replaces {backupToolLabel(selected.tool)}&apos;s current library with this backup.
                                        {' '}{backupToolLabel(selected.tool)} must be closed. Your current files are kept as timestamped safety copies.
                                    </span>
                                </span>
                            </label>

                            {needsTypedConfirm && applyToolLabel && (
                                <div className="brm-confirm">
                                    <span className="brm-option-detail">
                                        Type <strong>{applyToolLabel}</strong> to confirm:
                                    </span>
                                    <input
                                        type="text"
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                        placeholder={applyToolLabel}
                                        disabled={busy}
                                    />
                                </div>
                            )}

                            {busy && (
                                <div className="brm-progress">
                                    <span className="brm-muted">{progressLabel || 'Working…'}</span>
                                    <div className="brm-progress-track">
                                        <div
                                            className="brm-progress-fill"
                                            style={{ width: `${progressPercent ?? (progressLabel === 'Downloading and verifying' ? 0 : 100)}%` }}
                                        />
                                    </div>
                                    {progressPercent != null && <span className="brm-muted">{progressPercent}%</span>}
                                </div>
                            )}
                        </>
                    )}

                    {step === 3 && result && (
                        result.ok ? (
                            <div className="brm-result">
                                <p className="brm-lead">
                                    {kind !== 'missing' ? 'Restore complete'
                                        : result.stoppedEarly ? 'Restore stopped'
                                        : 'You’re all caught up'}
                                </p>
                                {kind === 'missing' ? (
                                    <>
                                        {result.missing?.files && (
                                            <p className="brm-muted">
                                                Music: {(result.missing.files.restored || []).length.toLocaleString()} restored
                                                {result.missing.files.skipped ? ` · ${result.missing.files.skipped.toLocaleString()} already here` : ''}
                                                {(result.missing.files.failed || []).length ? ` · ${(result.missing.files.failed || []).length.toLocaleString()} failed` : ''}.
                                            </p>
                                        )}
                                        {result.missing?.library && !result.missing.library.error && !result.missing.library.unsupported && (() => {
                                            const lib = result.missing.library;
                                            const bits = [];
                                            if (lib.tracksAdded) bits.push(`${lib.tracksAdded.toLocaleString()} ${lib.tracksAdded === 1 ? 'track' : 'tracks'} added`);
                                            if (lib.tracksEnriched) bits.push(`${lib.tracksEnriched.toLocaleString()} enriched from your backup`);
                                            const pls = (lib.playlistsCreated || 0) + (lib.playlistsUpdated || 0);
                                            if (pls) bits.push(`${pls.toLocaleString()} ${pls === 1 ? 'playlist' : 'playlists'} restored`);
                                            return (
                                                <p className="brm-muted">
                                                    MixGoat library: {bits.length ? `${bits.join(' · ')}.` : 'already up to date.'}
                                                </p>
                                            );
                                        })()}
                                        {result.missing?.library?.unsupported && (
                                            <p className="brm-muted">
                                                This backup predates library restore, so your tracks&apos; enrichment couldn&apos;t come
                                                back — update MixGoat on the computer that made it and back up again.
                                            </p>
                                        )}
                                        {result.missing?.library?.error && (
                                            <p className="brm-muted">
                                                Your MixGoat library couldn&apos;t be updated ({result.missing.library.error}) — your
                                                files and DJ apps were still restored. Run Restore again to retry.
                                            </p>
                                        )}
                                        {(result.missing?.dj?.targets || []).map(({ target, migration, ownInBackup, ownSurvived, ownTracksHere, missingOwnTracks, missingNeverBackedUp }) => {
                                            const note = mergeRestoreNote({ target, ownInBackup, ownSurvived, missingOwnTracks, missingNeverBackedUp });
                                            // State, not delta: "what of your backup is in the
                                            // app now". Writer add-counts read as "0 merged" on
                                            // an idempotent re-run and looked broken. Older
                                            // result shapes (no ownTracksHere) keep the old line.
                                            const line = ownTracksHere != null
                                                ? (ownTracksHere > 0
                                                    ? `${ownSurvived.toLocaleString()} ${ownSurvived === 1 ? 'playlist' : 'playlists'} and ${ownTracksHere.toLocaleString()} ${ownTracksHere === 1 ? 'track' : 'tracks'} from your backup are in place.`
                                                    : 'Nothing from its backup is on this computer yet.')
                                                : `${(migration?.targetResults?.[0]?.playlistsConverted ?? 0).toLocaleString()} playlists and ${(migration?.targetResults?.[0]?.tracksConverted ?? 0).toLocaleString()} tracks merged from your backup.`;
                                            return (
                                                <div className="brm-result-app" key={target}>
                                                    <span className="brm-result-app-name">{backupToolLabel(target)}</span>
                                                    <span className="brm-result-app-line">{line}</span>
                                                    {note && <span className="brm-result-note">{note}</span>}
                                                </div>
                                            );
                                        })}
                                        {result.missing?.dj?.nothingToRestore && (
                                            <p className="brm-muted">
                                                None of the backup’s tracks could be found on this computer, so no DJ data was written.
                                                Restore your music files first, then run this again.
                                            </p>
                                        )}
                                        {result.stoppedEarly && (
                                            <p className="brm-muted">
                                                The rest of your files are still safe in your backup — run Restore again
                                                any time and it picks up where it stopped.
                                            </p>
                                        )}
                                        {(result.missing?.files?.failed || []).length > 0 && (
                                            <p className="brm-muted">Failed files are still safe in your backup — run this again to retry them.</p>
                                        )}
                                        <p className="brm-muted">Nothing on this computer was removed or replaced.</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="brm-muted">{(result.restored || []).length} file{(result.restored || []).length === 1 ? '' : 's'} restored{result.destDir ? ` to ${result.destDir}` : ''}.</p>
                                        {(result.safetyCopies || []).length > 0 && (
                                            <p className="brm-muted">
                                                Your previous files were kept as safety copies ({result.safetyCopies.length}) next to the restored ones.
                                            </p>
                                        )}
                                        {(result.relink?.results?.[0]?.updated || 0) > 0 && (
                                            <p className="brm-muted">
                                                {result.relink.results[0].updated.toLocaleString()} track{result.relink.results[0].updated === 1 ? ' was' : 's were'} pointed at where the music lives on this computer.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="brm-result">
                                <p className="brm-lead">Restore failed</p>
                                <p className="brm-error">{result.error}</p>
                                <p className="brm-muted">Nothing was changed unless listed above — your current library is intact.</p>
                            </div>
                        )
                    )}
                </div>

                <div className="brm-footer">
                    {/* No Back mid-run — a disabled button that ignores clicks
                        reads as broken; while a restore runs, the ways out are
                        × (window closes, restore continues) or waiting. The
                        guided flow has no earlier screen, so only the per-app
                        destination step gets a Back. */}
                    {step === 3 && kind === 'dj_data' && !result && !busy && (
                        <button type="button" className="brm-secondary-btn" onClick={() => { setStep(2); setResult(null); }}>
                            Back
                        </button>
                    )}
                    {/* Stop is offered for the long leg only — downloading the
                        music files. The DJ-data merge that follows is seconds
                        long and half-applied DJ data is worse than waiting. */}
                    {step === 3 && kind === 'missing' && !result && busy && missingPhase === 'files' && (
                        <button type="button" className="brm-secondary-btn" onClick={stopRestore} disabled={stopping}>
                            {stopping ? 'Stopping…' : 'Stop restore'}
                        </button>
                    )}
                    <div className="brm-footer-spacer" />
                    {step === 2 && kind === 'missing' && !result && (
                        <button
                            type="button"
                            className="brm-primary-btn"
                            disabled={busy || restoreRunningElsewhere || (!scopeFiles && !missingTargets.length && !scopeLibrary)}
                            onClick={runMissingRestore}
                        >
                            {restoreRunningElsewhere ? 'Restore in progress…' : 'Restore'}
                        </button>
                    )}
                    {step === 3 && kind === 'dj_data' && !result && (
                        <button
                            type="button"
                            className="brm-primary-btn"
                            disabled={busy || !confirmOk}
                            onClick={runRestore}
                        >
                            {busy ? 'Restoring…' : 'Restore'}
                        </button>
                    )}
                    {result && (
                        <button type="button" className="brm-primary-btn" onClick={result.ok ? onHide : () => setResult(null)}>
                            {result.ok ? 'Done' : 'Try again'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// A DJ app has something to bring back when the portable backup's per-tool
// counts say so, or — for backups made before those counts existed — when a
// DJ-data snapshot for it is stored.
function toolHasBackup(preview, tool) {
    const per = preview?.model?.counts?.perTool;
    if (per) {
        const c = per[tool];
        return !!c && (c.playlists > 0 || c.tracks > 0);
    }
    return (preview?.tools || []).some((t) => t.tool === tool);
}

export default BackupRestoreModal;
